import React, { useState } from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Button,
    VStack,
    Select,
    Text,
    InputGroup,
    InputRightElement,
    Spinner
} from '@chakra-ui/react';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { hadlePreFilledFields } from '../ServiceRequest';

interface NewDocumentFormProps {
    templates: any[];
    handleNewDocumentData: (e: any) => void;
    handleCreateNewDocument: (e: any) => void;
}

const NewDocumentForm: React.FC<NewDocumentFormProps> = ({
    templates,
    handleNewDocumentData,
    handleCreateNewDocument,
}) => {

    const [project, setProject] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [isProjectValid, setIsProjectValid] = useState<boolean>(false);
    const [isLoadingProject, setIsLoadingProject] = useState<boolean>(false);


    const [formData, setFormData] = useState({
        projectId: '',
        name: '',
        templateName: '',
    });

    const handleGetProject = async (projectId: string) => {
        setIsLoadingProject(true);
        try {
            const result = await hadlePreFilledFields(projectId);
            if (result) {
                setProject(result);
                setIsProjectValid(true);
                setError(null);
            } else {
                setProject(null);
                setIsProjectValid(false);
                setError('Invalid project ID');
            }
        } catch {
            setProject(null);
            setIsProjectValid(false);
            setError('Invalid project ID');
        } finally {
            setIsLoadingProject(false);
        }
    };

    const isFormValid = !isLoadingProject && project !== null && formData.name.trim() !== '' && formData.templateName !== '';


    return (
        <Box as="form">
            <Text className="title" fontSize="2xl" mb={6}>
                Create new document
            </Text>
            <VStack spacing={4} align="stretch">
                <FormControl className="input-field" mb={4} isInvalid={!!error}>
                    <FormLabel>Project ID</FormLabel>
                    <InputGroup>
                        <Input
                            name="projectId"
                            value={formData.projectId}
                            onChange={(e) => {
                                const numericValue = e.target.value.replace(/\D/g, '');
                                setFormData((prev) => ({ ...prev, projectId: numericValue }));
                                e.target.value = numericValue;
                                handleNewDocumentData(e);

                                if (numericValue.length >= 5) {
                                    handleGetProject(numericValue);
                                } else {
                                    setProject(null);
                                    setIsProjectValid(false);
                                    setError(null);
                                }
                            }}
                            className="dark-input"
                        />
                        <InputRightElement>
                            {isLoadingProject ? (
                                <Spinner size="sm" color="black" />
                            ) : error ? (
                                <MdError color="#f56565" size="1.2em" />
                            ) : isProjectValid ? (
                                <MdCheckCircle color="#48bb78" size="1.4em" />
                            ) : null}
                        </InputRightElement>
                    </InputGroup>
                    {error && (
                        <Text color="red.500" fontSize="sm">
                            {error}
                        </Text>
                    )}
                </FormControl>
                <FormControl className="input-field" mb={4}>
                    <FormLabel>Document name</FormLabel>
                    <Input
                        name="name"
                        value={formData.name}
                        onChange={(e) => {
                            setFormData((prev) => ({ ...prev, name: e.target.value }));
                            handleNewDocumentData(e);
                        }}
                        className="dark-input"
                    />
                </FormControl>
                <FormControl className="input-field" mb={4}>
                    <FormLabel>Document description</FormLabel>
                    <Input
                        name="description"
                        onChange={handleNewDocumentData}
                        className="dark-input"
                    />
                </FormControl>
                <FormControl className="input-field" mb={4}>
                    <FormLabel>Please select a template:</FormLabel>
                    <Select
                        name="templateName"
                        value={formData.templateName}
                        onChange={(e) => {
                            setFormData((prev) => ({ ...prev, templateName: e.target.value }));
                            handleNewDocumentData(e);
                        }}
                        className="select"
                        
                    >
                        <option value="" disabled></option>
                        {templates.map((template) => (
                            <option key={template.name} value={template.name}>
                                {template.alias}
                            </option>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    className="el-btn"
                    mt={5}
                    onClick={handleCreateNewDocument}
                    isDisabled={!isFormValid}
                >
                    Create document
                </Button>
            </VStack>
        </Box>
    );
};

export default NewDocumentForm;